import { useRef, useState } from "react";
import "./style.css";
import EmojiPickerBackgrounds from "../createPostPopup/EmojiPickerBackgrounds";
import AddToYourPost from "../createPostPopup/AddToYourPost";
import ImagePreview from "../createPostPopup/ImagePreview";
import FeelingActivitySelector from "../createPostPopup/FeelingActivitySelector";
import useClickOutside from "../../helpers/clickOutside";
import { createGroupPost } from "../../functions/post";
import PulseLoader from "react-spinners/PulseLoader";
import PostError from "../createPostPopup/PostError";
import dataURItoBlob from "../../helpers/dataURItoBlob";
import { uploadImages } from "../../functions/uploadImages";
export default function CreateGroupPostPopup({ 
  user, 
  setVisible, 
  groupId, 
  groupName, 
  onPostCreated 
}) {
  const popup = useRef(null);
  
  // Helper functions for feeling/activity display
  const getFeelingEmoji = (feelingId) => {
    const feelings = {
      "happy": "😊", "sad": "😢", "angry": "😠", "excited": "🤩", "grateful": "🙏",
      "blessed": "🙌", "loved": "💕", "thankful": "🙏", "amazed": "😲", "proud": "😎",
      "hopeful": "✨", "confident": "💪", "peaceful": "😌", "joyful": "😄", "inspired": "💡",
      "motivated": "🚀", "energetic": "⚡", "relaxed": "😴", "focused": "🎯", "creative": "🎨",
      "adventurous": "🗺️", "curious": "🤔", "surprised": "😮", "nostalgic": "📷", "romantic": "💖"
    };
    return feelings[feelingId] || "😊";
  };

  const getFeelingLabel = (feelingId) => {
    const feelings = {
      "happy": "Happy", "sad": "Sad", "angry": "Angry", "excited": "Excited", "grateful": "Grateful",
      "blessed": "Blessed", "loved": "Loved", "thankful": "Thankful", "amazed": "Amazed", "proud": "Proud",
      "hopeful": "Hopeful", "confident": "Confident", "peaceful": "Peaceful", "joyful": "Joyful", "inspired": "Inspired",
      "motivated": "Motivated", "energetic": "Energetic", "relaxed": "Relaxed", "focused": "Focused", "creative": "Creative",
      "adventurous": "Adventurous", "curious": "Curious", "surprised": "Surprised", "nostalgic": "Nostalgic", "romantic": "Romantic"
    };
    return feelings[feelingId] || "Happy";
  };

  const getActivityEmoji = (activityId) => {
    const activities = {
      "traveling": "✈️", "working": "💼", "studying": "📚", "exercising": "🏃‍♂️", "cooking": "👨‍🍳",
      "reading": "📖", "watching": "📺", "listening": "🎧", "playing": "🎮", "shopping": "🛒",
      "cleaning": "🧹", "gardening": "🌱", "painting": "🎨", "writing": "✍️", "dancing": "💃",
      "singing": "🎤", "photography": "📸", "gaming": "🎮", "meditation": "🧘‍♂️", "yoga": "🧘‍♀️",
      "running": "🏃‍♀️", "cycling": "🚴‍♂️", "swimming": "🏊‍♂️", "hiking": "🏔️", "camping": "⛺",
      "fishing": "🎣", "golfing": "⛳", "tennis": "🎾", "basketball": "🏀", "football": "⚽",
      "volleyball": "🏐", "baseball": "⚾", "soccer": "⚽", "hockey": "🏒", "boxing": "🥊",
      "martial_arts": "🥋", "weightlifting": "🏋️‍♂️", "pilates": "🧘‍♀️", "zumba": "💃", "crossfit": "💪",
      "rock_climbing": "🧗‍♂️", "skateboarding": "🛹", "surfing": "🏄‍♂️", "skiing": "⛷️", "snowboarding": "🏂",
      "ice_skating": "⛸️", "rollerblading": "🛼", "parkour": "🤸‍♂️", "gymnastics": "🤸‍♀️", "cheerleading": "📣",
      "marching_band": "🎺", "orchestra": "🎻", "choir": "🎤", "karaoke": "🎤"
    };
    return activities[activityId] || "🎯";
  };

  const getActivityLabel = (activityId) => {
    const activities = {
      "traveling": "Traveling", "working": "Working", "studying": "Studying", "exercising": "Exercising", "cooking": "Cooking",
      "reading": "Reading", "watching": "Watching", "listening": "Listening", "playing": "Playing", "shopping": "Shopping",
      "cleaning": "Cleaning", "gardening": "Gardening", "painting": "Painting", "writing": "Writing", "dancing": "Dancing",
      "singing": "Singing", "photography": "Photography", "gaming": "Gaming", "meditation": "Meditation", "yoga": "Yoga",
      "running": "Running", "cycling": "Cycling", "swimming": "Swimming", "hiking": "Hiking", "camping": "Camping",
      "fishing": "Fishing", "golfing": "Golfing", "tennis": "Tennis", "basketball": "Basketball", "football": "Football",
      "volleyball": "Volleyball", "baseball": "Baseball", "soccer": "Soccer", "hockey": "Hockey", "boxing": "Boxing",
      "martial_arts": "Martial Arts", "weightlifting": "Weightlifting", "pilates": "Pilates", "zumba": "Zumba", "crossfit": "CrossFit",
      "rock_climbing": "Rock Climbing", "skateboarding": "Skateboarding", "surfing": "Surfing", "skiing": "Skiing", "snowboarding": "Snowboarding",
      "ice_skating": "Ice Skating", "rollerblading": "Rollerblading", "parkour": "Parkour", "gymnastics": "Gymnastics", "cheerleading": "Cheerleading",
      "marching_band": "Marching Band", "orchestra": "Orchestra", "choir": "Choir", "karaoke": "Karaoke"
    };
    return activities[activityId] || "Activity";
  };
  const [text, setText] = useState("");
  const [showPrev, setShowPrev] = useState(false);
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [background, setBackground] = useState("");
  const [selectedFeeling, setSelectedFeeling] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showFeelingActivitySelector, setShowFeelingActivitySelector] = useState(false);
  
  // Function to reset all form states
  const resetForm = () => {
    setText("");
    setImages([]);
    setVideos([]);
    setBackground("");
    setSelectedFeeling(null);
    setSelectedActivity(null);
    setShowFeelingActivitySelector(false);
    setShowPrev(false);
    setError("");
  };

  useClickOutside(popup, () => {
    resetForm();
    setVisible(false);
  }, [".feeling_activity_selector", ".feeling_activity_overlay"]);

  const postSubmit = async () => {
    if (!groupId) {
      setError("Group information is missing");
      return;
    }

    if (background) {
      setLoading(true);
      const response = await createGroupPost(
        null,
        background,
        text,
        null,
        user.id,
        groupId,
        user.token,
        selectedFeeling,
        selectedActivity
      );
      setLoading(false);
      if (response.status === "ok") {
        resetForm();
        setVisible(false);
        if (onPostCreated) onPostCreated(response.data);
      } else {
        setError(response.message);
      }
    } else if (images && images.length) {
      setLoading(true);
      const postImages = images.map((img, idx) => {
        return dataURItoBlob(img, `group_post_image_${Date.now()}_${idx}`);
      });
      const path = `${user.username}/group_posts/${groupId}`;
      let formData = new FormData();
      formData.append("path", path);
      postImages.forEach((image) => {
        formData.append("file", image);
      });
              const imageResponse = await uploadImages(formData, path, user.token);

      const res = await createGroupPost(
        null,
        null,
        text,
        imageResponse,
        user.id,
        groupId,
        user.token,
        selectedFeeling,
        selectedActivity
      );
      setLoading(false);
      if (res.status === "ok") {
        resetForm();
        setVisible(false);
        if (onPostCreated) onPostCreated(res.data);
      } else {
        setError(res.message);
      }
    } else if (videos && videos.length) {
      setLoading(true);
      const postVideos = videos.map((video) => {
        return dataURItoBlob(video);
      });
      const path = `${user.username}/group_videos/${groupId}`;
      let formData = new FormData();
      formData.append("path", path);
      postVideos.forEach((video) => {
        formData.append("file", video);
      });
              const videoResponse = await uploadImages(formData, path, user.token);

      const res = await createGroupPost(
        null,
        null,
        text,
        videoResponse,
        user.id,
        groupId,
        user.token,
        selectedFeeling,
        selectedActivity
      );
      setLoading(false);
      if (res.status === "ok") {
        resetForm();
        setVisible(false);
        if (onPostCreated) onPostCreated(res.data);
      } else {
        setError(res.message);
      }
    } else if (text) {
      setLoading(true);
      const response = await createGroupPost(
        null,
        null,
        text,
        null,
        user.id,
        groupId,
        user.token,
        selectedFeeling,
        selectedActivity
      );
      setLoading(false);
      if (response.status === "ok") {
        resetForm();
        setVisible(false);
        if (onPostCreated) onPostCreated(response.data);
      } else {
        setError(response.message);
      }
    } else {
      setError("Please add some text, images, videos, or select a background before posting");
    }
  };

  return (
    <div className="blur">
      <div className="postBox" ref={popup}>
        <div className="box_header">
          <div
            className="small_circle"
            onClick={() => {
              resetForm();
              setVisible(false);
            }}
          >
            <i className="exit_icon"></i>
          </div>
          <span>Create publication in {groupName}</span>
        </div>
        {error && <PostError error={error} setError={setError} />}
        <div className="box_profile">
          <img src={user.picture} alt="" className="box_profile_img" />
          <div className="box_col">
            <div className="box_profile_name">
              {user.first_name} {user.last_name}
            </div>
            <div className="box_privacy">
              <img src="../../../icons/public.png" alt="" />
              <span>Posting to {groupName}</span>
              <i className="arrowDown_icon"></i>
            </div>
          </div>
        </div>

        {!showPrev ? (
          <>
            <EmojiPickerBackgrounds
              text={text}
              user={user}
              setText={setText}
              type2
              background={background}
              setBackground={setBackground}
            />
          </>
        ) : (
          <ImagePreview
            text={text}
            user={user}
            setText={setText}
            images={images}
            setImages={setImages}
            videos={videos}
            setVideos={setVideos}
            setShowPrev={setShowPrev}
            setError={setError}
          />
        )}
        <AddToYourPost 
          setShowPrev={setShowPrev} 
          onFeelingActivityClick={() => setShowFeelingActivitySelector(true)}
        />
        
        {/* Display selected feeling/activity */}
        {(selectedFeeling || selectedActivity) && (
          <div className="selected_feeling_activity">
            {selectedFeeling && (
              <div className="selected_item_display">
                <span className="selected_emoji notranslate" translate="no">
                  {getFeelingEmoji(selectedFeeling)}
                </span>
                <span className="selected_text">
                  {getFeelingLabel(selectedFeeling)}
                </span>
              </div>
            )}
            {selectedActivity && (
              <div className="selected_item_display">
                <span className="selected_emoji notranslate" translate="no">
                  {getActivityEmoji(selectedActivity)}
                </span>
                <span className="selected_text">
                  {getActivityLabel(selectedActivity)}
                </span>
              </div>
            )}
          </div>
        )}
        
        <button
          className="post_submit"
          onClick={() => {
            postSubmit();
          }}
          disabled={loading}
        >
          {loading ? (
            <PulseLoader color="#fff" size={5} />
          ) : (
            "Post"
          )}
        </button>
        
        {/* Feeling/Activity Selector Modal */}
        {showFeelingActivitySelector && (
          <FeelingActivitySelector
            selectedFeeling={selectedFeeling}
            selectedActivity={selectedActivity}
            onFeelingSelect={setSelectedFeeling}
            onActivitySelect={setSelectedActivity}
            onClose={() => setShowFeelingActivitySelector(false)}
          />
        )}
      </div>
    </div>
  );
} 